import { useCallback, useEffect, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import api, { fmtGNF, fmtDate, apiError, logError } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { AmountInput } from "../components/AmountInput";

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [debts, setDebts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", initial_debt: "" });
  const [payId, setPayId] = useState(null);
  const [payAmount, setPayAmount] = useState("");

  const load = useCallback(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch((e) => logError(e));
    api.get("/supplier-debts").then((r) => setDebts(r.data)).catch((e) => logError(e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/suppliers", {
        name: form.name, phone: form.phone, initial_debt: parseFloat(form.initial_debt) || 0,
      });
      toast.success(form.initial_debt ? "Fournisseur créé — dette antérieure enregistrée et tracée" : "Fournisseur créé");
      setShowAdd(false); setForm({ name: "", phone: "", initial_debt: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doPay = async () => {
    try {
      await api.post(`/supplier-debts/${payId}/pay`, { amount: parseFloat(payAmount) });
      toast.success("Paiement enregistré — dépense « Paiements fournisseurs » créée automatiquement");
      setPayId(null); setPayAmount(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDebts = debts.filter((d) => d.payment_status !== "paid");

  return (
    <div className="space-y-4 fade-in" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-2xl">Fournisseurs</h1>
        <button data-testid="add-supplier-btn" onClick={() => setShowAdd(true)} className="btn-ghost py-2 px-4 flex items-center gap-1.5 text-sm"><Plus size={16} /> Nouveau</button>
      </div>

      {openDebts.length > 0 && (
        <div className="kcard p-5">
          <h3 className="font-display font-bold mb-3">Dettes en cours (vous devez)</h3>
          <div className="space-y-2">
            {openDebts.map((d) => (
              <div key={d.id} data-testid="supplier-debt-row" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#F6F4EF]/60 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{d.supplier_name}</p>
                  <p className="text-xs text-gray-500">{d.description} · {fmtDate(d.created_at)}</p>
                </div>
                <span className="font-semibold text-sm tabular-nums">{fmtGNF(d.amount - d.amount_paid)}</span>
                <StatusBadge status={d.payment_status} />
                <button data-testid="debt-pay-btn" onClick={() => { setPayId(d.id); setPayAmount(""); }} className="text-xs text-[#2E7D5B] font-semibold">Régler</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {suppliers.map((s) => (
          <div key={s.id} data-testid="supplier-row" className="kcard p-4 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{s.name} <span className="font-normal text-gray-500">· {s.phone}</span></p>
              <p className="text-xs text-gray-500">Total acheté : {fmtGNF(s.total_spent)}</p>
              {s.categories?.length > 0 && (
                <p data-testid="supplier-categories" className="text-xs text-gray-400 mt-0.5">
                  Fournit : {s.categories.join(", ")}
                </p>
              )}
            </div>
            {s.outstanding > 0
              ? <span className="badge-unpaid px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums">Vous devez {fmtGNF(s.outstanding)}</span>
              : <span className="badge-paid px-2.5 py-1 rounded-full text-xs font-semibold">À jour</span>}
          </div>
        ))}
        {suppliers.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucun fournisseur. Ils sont créés lors des réceptions de stock ou ici.</div>}
      </div>

      {showAdd && (
        <ModalShell size="max-w-sm" onClose={() => setShowAdd(false)}>
            <h3 className="font-display font-bold text-lg">Nouveau fournisseur</h3>
            <input data-testid="supplier-name-input" className="kinput" placeholder="Nom (obligatoire)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="supplier-phone-input" className="kinput" placeholder="Téléphone (obligatoire)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <div>
              <AmountInput data-testid="supplier-initial-debt-input" onOverflow={(m) => toast.error(m)} placeholder="Dette déjà existante (optionnel, GNF)" value={form.initial_debt} onChange={(v) => setForm({ ...form, initial_debt: v })} />
              <p className="text-xs text-gray-500 mt-1">Ce que vous devez déjà à ce fournisseur avant d'utiliser Kavizio. Disponible uniquement à la création.</p>
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setShowAdd(false)}>Annuler</button>
              <button data-testid="supplier-create-btn" className="btn-amber flex-1 py-2.5" disabled={!form.name || !form.phone} onClick={create}>Créer</button>
            </div>
          </ModalShell>
      )}

      {payId && (
        <ModalShell size="max-w-sm" onClose={() => setPayId(null)}>
            <h3 className="font-display font-bold text-lg mb-3">Régler la dette</h3>
            <AmountInput data-testid="debt-pay-amount-input" onOverflow={(m) => toast.error(m)} className="mb-3" placeholder="Montant payé (GNF)" value={payAmount} onChange={setPayAmount} />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setPayId(null)}>Retour</button>
              <button data-testid="debt-pay-confirm-btn" className="btn-amber flex-1 py-2.5" disabled={!payAmount} onClick={doPay}>Payer</button>
            </div>
          </ModalShell>
      )}
    </div>
  );
}
